import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RosterTransfer, TransferFilters } from '../../models/RosterTransfer';
import { fetchTransfers, fetchDivisions } from '../../services/TransferService';
import { fetchTeamByTeamId } from '../../services/TeamService';
import { Team } from '../../models/Team';
import { currentSeason, divisionMappings } from '../../Constants';
import Loading from '../../components/Loading';
import PlayerImage from '../../components/PlayerImage';
import { useDarkMode } from '../../hooks/useDarkmode';
import SiteBanner from '../../components/SiteBanner/SiteBanner';
import styles from './TransfersPage.module.css';
import '../../styles/theme.css';

const TransfersPage: React.FC = () => {
    const { t } = useTranslation(['pages', 'common']);
    const isDark = useDarkMode();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Data state
    const [transfers, setTransfers] = useState<RosterTransfer[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [teams, setTeams] = useState<{ [key: string]: Team }>({});

    // Filter state
    const [selectedSeason, setSelectedSeason] = useState<number>(currentSeason);
    const [selectedDivision, setSelectedDivision] = useState<string>('');
    const [teamSearch, setTeamSearch] = useState<string>('');
    const [playerSearch, setPlayerSearch] = useState<string>('');
    const [divisions, setDivisions] = useState<string[]>([]);

    // Pagination state
    const [page, setPage] = useState(0);
    const pageSize = 25;

    // Sort state
    const [dateSortOrder, setDateSortOrder] = useState<'desc' | 'asc'>('desc');

    // Generate seasons array (18 to currentSeason)
    const seasons = useMemo(() => {
        const result = [];
        for (let s = currentSeason; s >= 18; s--) {
            result.push(s);
        }
        return result;
    }, []);

    // Handle resize for mobile detection
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch divisions on mount
    useEffect(() => {
        fetchDivisions()
            .then(divs => setDivisions(divs.filter(d => d.toLowerCase() !== 'playoff')))
            .catch(err => console.error('Failed to fetch divisions:', err));
    }, []);

    // Fetch transfers when filters change
    const loadTransfers = useCallback(async () => {
        setLoading(true);
        setError(null);

        const filters: TransferFilters = {
            season: selectedSeason,
            limit: pageSize,
            offset: page * pageSize,
        };

        if (selectedDivision) {
            filters.division = selectedDivision;
        }
        if (playerSearch.trim()) {
            filters.playerName = playerSearch.trim();
        }
        if (teamSearch.trim()) {
            filters.teamName = teamSearch.trim();
        }

        try {
            const response = await fetchTransfers(filters);

            setTransfers(response.transfers);
            setTotalCount(response.totalCount);
        } catch (err) {
            setError('Failed to load transfers');
            console.error('Error loading transfers:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedSeason, selectedDivision, teamSearch, playerSearch, page]);

    // Debounced loading effect
    useEffect(() => {
        const timer = setTimeout(() => {
            loadTransfers();
        }, 300);

        return () => clearTimeout(timer);
    }, [loadTransfers]);

    // Fetch team data for logos when transfers change
    useEffect(() => {
        const teamIdsToFetch: string[] = [];

        transfers.forEach(transfer => {
            if (transfer.fromTeamId && !teams[transfer.fromTeamId]) {
                teamIdsToFetch.push(transfer.fromTeamId);
            }
            if (transfer.toTeamId && !teams[transfer.toTeamId]) {
                teamIdsToFetch.push(transfer.toTeamId);
            }
        });

        // Remove duplicates
        const uniqueTeamIds = Array.from(new Set(teamIdsToFetch));

        if (uniqueTeamIds.length === 0) return;

        const fetchTeams = async () => {
            const fetchedTeams: { [key: string]: Team } = {};
            await Promise.all(
                uniqueTeamIds.map(async (teamId) => {
                    try {
                        const team = await fetchTeamByTeamId(teamId);
                        if (team) {
                            fetchedTeams[teamId] = team;
                        }
                    } catch (err) {
                        console.error(`Failed to fetch team ${teamId}:`, err);
                    }
                })
            );
            setTeams(prev => ({ ...prev, ...fetchedTeams }));
        };

        fetchTeams();
    }, [transfers]);

    // Sort transfers by date
    const sortedTransfers = useMemo(() => {
        return [...transfers].sort((a, b) => {
            const dateA = new Date(a.transferDate).getTime();
            const dateB = new Date(b.transferDate).getTime();
            return dateSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    }, [transfers, dateSortOrder]);

    // Toggle date sort order
    const toggleDateSort = () => {
        setDateSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    // Format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('da-DK', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get transfer type badge
    const getTransferTypeBadge = (type: string) => {
        switch (type) {
            case 'join':
                return <span className={`${styles.badge} ${styles.badgeJoin}`}>Joined</span>;
            case 'leave':
                return <span className={`${styles.badge} ${styles.badgeLeave}`}>Left</span>;
            case 'transfer':
                return <span className={`${styles.badge} ${styles.badgeTransfer}`}>Transfer</span>;
            default:
                return <span className={styles.badge}>{type}</span>;
        }
    };

    // Get formatted division name
    const getDivisionName = (division: string) => {
        return divisionMappings.get(division) || division;
    };

    // Render team info with logo and division from fetched team data
    const renderTeamInfo = (teamId: string | null, teamName: string | null, showDivision: boolean = true) => {
        const team = teamId ? teams[teamId] : undefined;
        // Rows recorded when a team dropped out of the OverPass feed carry no team name, so fall
        // back to the name we already fetched by id rather than showing the row as an empty dash.
        const displayName = teamName || team?.teamName;

        if (!teamId || !displayName) {
            return <span className={styles.noTeam}>-</span>;
        }

        const logoUrl = team?.teamLogo;
        const division = team?.division ? getDivisionName(team.division) : null;

        return (
            <Link to={`/team/${teamId}`} className={styles.teamLink}>
                {logoUrl && (
                    <img
                        src={logoUrl}
                        alt={displayName}
                        className={styles.teamLogo}
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                    />
                )}
                <span>
                    {displayName}
                    {showDivision && division && (
                        <span className={styles.teamDivision}> ({division})</span>
                    )}
                </span>
            </Link>
        );
    };

    // Render transfer row for desktop
    const renderTransferRow = (transfer: RosterTransfer) => (
        <tr key={transfer.transferId} className={styles.transferRow}>
            <td className={styles.playerCell}>
                <Link to={`/player/${transfer.playerSteamId}`} className={styles.playerLink}>
                    <PlayerImage id={transfer.playerSteamId} width={32} />
                    <span className={styles.playerName}>{transfer.playerName || 'Unknown'}</span>
                </Link>
            </td>
            <td className={styles.typeCell}>
                {getTransferTypeBadge(transfer.transferType)}
            </td>
            <td className={styles.fromTeamCell}>
                {transfer.transferType === 'join'
                    ? <span className={styles.noTeam}>-</span>
                    : renderTeamInfo(transfer.fromTeamId, transfer.fromTeamName)
                }
            </td>
            <td className={styles.arrowCell}>
                <span className={styles.arrow}>→</span>
            </td>
            <td className={styles.toTeamCell}>
                {transfer.transferType === 'leave'
                    ? <span className={styles.noTeam}>-</span>
                    : renderTeamInfo(transfer.toTeamId, transfer.toTeamName)
                }
            </td>
            <td className={styles.dateCell}>
                {formatDate(transfer.transferDate)}
            </td>
        </tr>
    );

    // Render transfer card for mobile
    const renderTransferCard = (transfer: RosterTransfer) => (
        <div key={transfer.transferId} className={styles.transferCard}>
            <div className={styles.cardHeader}>
                <Link to={`/player/${transfer.playerSteamId}`} className={styles.playerLink}>
                    <PlayerImage id={transfer.playerSteamId} width={40} />
                    <span className={styles.playerName}>{transfer.playerName || 'Unknown'}</span>
                </Link>
                {getTransferTypeBadge(transfer.transferType)}
            </div>
            <div className={styles.cardBody}>
                <div className={styles.teamsRow}>
                    <div className={styles.teamColumn}>
                        {transfer.transferType === 'join'
                            ? <span className={styles.noTeam}>-</span>
                            : renderTeamInfo(transfer.fromTeamId, transfer.fromTeamName)
                        }
                    </div>
                    <span className={styles.arrow}>→</span>
                    <div className={styles.teamColumn}>
                        {transfer.transferType === 'leave'
                            ? <span className={styles.noTeam}>-</span>
                            : renderTeamInfo(transfer.toTeamId, transfer.toTeamName)
                        }
                    </div>
                </div>
                <div className={styles.cardFooter}>
                    <span className={styles.date}>{formatDate(transfer.transferDate)}</span>
                </div>
            </div>
        </div>
    );

    // Calculate pagination
    const totalPages = Math.ceil(totalCount / pageSize);
    const hasMore = page < totalPages - 1;

    return (
        <div className={`${styles.container} ${isDark ? styles.dark : ''}`}>
            <SiteBanner />
            <h1 className={styles.title}>Transfers</h1>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label htmlFor="season-select">Season</label>
                    <select
                        id="season-select"
                        value={selectedSeason}
                        onChange={(e) => { setSelectedSeason(Number(e.target.value)); setPage(0); }}
                        className={styles.select}
                    >
                        {seasons.map(s => (
                            <option key={s} value={s}>Season {s}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="division-select">Division</label>
                    <select
                        id="division-select"
                        value={selectedDivision}
                        onChange={(e) => { setSelectedDivision(e.target.value); setPage(0); }}
                        className={styles.select}
                    >
                        <option value="">All Divisions</option>
                        {divisions.map(d => (
                            <option key={d} value={d}>{getDivisionName(d)}</option>
                        ))}
                    </select>
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="team-search">Team</label>
                    <input
                        id="team-search"
                        type="text"
                        value={teamSearch}
                        onChange={(e) => { setTeamSearch(e.target.value); setPage(0); }}
                        placeholder="Search team..."
                        className={styles.input}
                    />
                </div>

                <div className={styles.filterGroup}>
                    <label htmlFor="player-search">Player</label>
                    <input
                        id="player-search"
                        type="text"
                        value={playerSearch}
                        onChange={(e) => { setPlayerSearch(e.target.value); setPage(0); }}
                        placeholder="Search player..."
                        className={styles.input}
                    />
                </div>
            </div>

            {/* Stats summary */}
            <div className={styles.stats}>
                <span>{totalCount} transfers found</span>
            </div>

            {/* Content */}
            {loading ? (
                <Loading />
            ) : error ? (
                <div className={styles.error}>{error}</div>
            ) : transfers.length === 0 ? (
                <div className={styles.noData}>No transfers found for the selected filters.</div>
            ) : isMobile ? (
                /* Mobile: Cards layout */
                <div className={styles.cardContainer}>
                    {sortedTransfers.map(renderTransferCard)}
                </div>
            ) : (
                /* Desktop: Table layout */
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Player</th>
                                <th>Type</th>
                                <th>From</th>
                                <th></th>
                                <th>To</th>
                                <th className={styles.sortableHeader} onClick={toggleDateSort}>
                                    Date {dateSortOrder === 'desc' ? '↓' : '↑'}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTransfers.map(renderTransferRow)}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && transfers.length > 0 && totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className={styles.pageButton}
                    >
                        Previous
                    </button>
                    <span className={styles.pageInfo}>
                        Page {page + 1} of {totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={!hasMore}
                        className={styles.pageButton}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default TransfersPage;
